import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Activity, User, Filter, RefreshCw, Mail } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Human-readable action labels + icons ──────────────────────────────────
const ACTION_META = {
  sign_in:                  { label: "signed in",                    tone: "bg-[#C6D21F] text-[#0D2F4D]" },
  sign_out:                 { label: "signed out",                   tone: "bg-white text-[#0D2F4D] border border-[#D6DCE3]" },
  lead_status_change:       { label: "updated lead status",          tone: "bg-[#0D2F4D] text-white" },
  lead_mark_settled:        { label: "marked lead as settled",       tone: "bg-[#C6D21F] text-[#0D2F4D]" },
  client_checklist_toggle:  { label: "ticked a checklist step",      tone: "bg-[#ECF0F4] text-[#0D2F4D]" },
  client_document_review:   { label: "reviewed a client document",   tone: "bg-[#ECF0F4] text-[#0D2F4D]" },
  client_note_update:       { label: "updated client notes",         tone: "bg-[#ECF0F4] text-[#0D2F4D]" },
  client_flag_toggle:       { label: "flagged / unflagged a client", tone: "bg-[#FDECEC] text-[#B91C1C]" },
  client_resend_login:      { label: "resent portal login link",     tone: "bg-[#ECF0F4] text-[#0D2F4D]" },
  xero_connect:             { label: "connected Xero",               tone: "bg-[#C6D21F] text-[#0D2F4D]" },
  xero_disconnect:          { label: "disconnected Xero",            tone: "bg-[#FDECEC] text-[#B91C1C]" },
};

function actionMeta(k) {
  return ACTION_META[k] || { label: k.replaceAll("_", " "), tone: "bg-[#ECF0F4] text-[#0D2F4D]" };
}

function timeAgo(iso) {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return iso; }
}

function ChangesLine({ action, changes }) {
  if (!changes || Object.keys(changes).length === 0) return null;
  if (action === "lead_status_change") {
    return (
      <span className="text-xs text-[#6F8FA8]">
        <b className="text-[#0D2F4D]">{changes.from || "—"}</b> → <b className="text-[#0D2F4D]">{changes.to}</b>
      </span>
    );
  }
  if (action === "client_checklist_toggle") {
    return (
      <span className="text-xs text-[#6F8FA8]">
        <b className="text-[#0D2F4D]">{changes.step}</b> · {changes.done ? "done" : "un-ticked"}
      </span>
    );
  }
  if (action === "client_document_review") {
    return (
      <span className="text-xs text-[#6F8FA8]">
        {changes.category ? <>[{changes.category}] </> : null}
        marked as <b className="text-[#0D2F4D]">{changes.status}</b>
        {changes.note ? <> — “{changes.note}”</> : null}
      </span>
    );
  }
  if (action === "client_flag_toggle") {
    return (
      <span className="text-xs text-[#6F8FA8]">
        {changes.flagged ? "flagged" : "un-flagged"}
      </span>
    );
  }
  if (action === "lead_mark_settled") {
    return (
      <span className="text-xs text-[#6F8FA8]">
        Team email sent: <b className="text-[#0D2F4D]">{changes.klaviyo_fired || changes.email_fired ? "yes" : "no"}</b>
      </span>
    );
  }
  return (
    <span className="text-xs text-[#6F8FA8]">
      {Object.entries(changes).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(" · ")}
    </span>
  );
}

export const ActivityTab = ({ pw }) => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [sendingDigest, setSendingDigest] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "200");
      if (action) params.set("action", action);
      if (actor) params.set("actor", actor);
      const [a, s] = await Promise.all([
        axios.get(`${API}/admin/activity?${params.toString()}`, {
          headers: { "X-Admin-Password": pw },
        }),
        axios.get(`${API}/admin/activity/summary`, {
          headers: { "X-Admin-Password": pw },
        }),
      ]);
      setItems(a.data?.activity || []);
      setSummary(s.data || null);
    } catch (e) {
      const msg = e?.response?.status === 401
        ? "Session expired — please sign in again."
        : (e?.response?.data?.detail || "Failed to load activity log.");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const sendDigestNow = async () => {
    if (!window.confirm("Send the daily digest email to the team right now?\n\nThis fires the 'Admin Daily Digest' event with the last 24h of activity.")) return;
    setSendingDigest(true);
    try {
      const res = await axios.post(
        `${API}/admin/activity/digest/send-now?window_hours=24&force=true`,
        {},
        { headers: { "X-Admin-Password": pw } },
      );
      if (res.data?.sent) {
        toast.success(`Digest fired — ${res.data.total_actions} actions summarised.`);
      } else {
        toast(`Digest not sent: ${res.data?.reason || "unknown"}`, { icon: "ℹ️" });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send digest.");
    } finally {
      setSendingDigest(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action, actor]);

  const actionOptions = useMemo(() => {
    const set = new Set(items.map((i) => i.action));
    return Array.from(set).sort();
  }, [items]);

  const actorOptions = useMemo(() => summary?.by_actor?.map((a) => a.actor_email) || [], [summary]);

  return (
    <div data-testid="activity-tab">
      <div className="mb-6 flex items-center gap-3">
        <Activity size={22} className="text-[#0D2F4D]" />
        <h2 className="font-display text-2xl font-extrabold text-[#0D2F4D]">Activity log</h2>
        <span className="text-xs font-bold uppercase tracking-widest bg-[#ECF0F4] text-[#6F8FA8] px-2 py-0.5 rounded-full">
          {items.length}
        </span>
        <button
          onClick={load}
          data-testid="activity-refresh"
          className="ml-auto inline-flex items-center gap-2 text-sm text-[#0D2F4D] hover:text-[#C6D21F]"
        >
          <RefreshCw size={14} /> Refresh
        </button>
        <button
          onClick={sendDigestNow}
          disabled={sendingDigest}
          data-testid="activity-send-digest"
          className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-[#0D2F4D] text-white hover:bg-[#1a4063] disabled:opacity-50 font-semibold"
        >
          <Mail size={14} /> {sendingDigest ? "Sending…" : "Send digest now"}
        </button>
      </div>
      <p className="text-sm text-[#6F8FA8] mb-6">
        Every mutation from the admin workspace — lead status changes, checklist
        ticks, document reviews, client flags, integrations connects, sign-ins.
        Rolls back 30 days. Daily digest auto-fires at <b>8am AEDT</b> to{" "}
        <b className="text-[#0D2F4D]">hello@theditchcollective.co.nz</b>.
      </p>

      {/* Rollup pills */}
      {summary && (
        <div className="grid md:grid-cols-2 gap-4 mb-6" data-testid="activity-summary">
          <div className="bg-white border border-[#E4E7EA] rounded-2xl p-5">
            <p className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold mb-3">
              Top actors (30d)
            </p>
            <div className="space-y-2">
              {(summary.by_actor || []).slice(0, 5).map((a) => (
                <div key={a.actor_email} className="flex items-center justify-between text-sm">
                  <span className="inline-flex items-center gap-2">
                    <User size={12} className="text-[#6F8FA8]" />
                    <span className="text-[#0D2F4D] font-semibold">{a.actor_email}</span>
                  </span>
                  <span className="text-[#6F8FA8]">{a.count} · {timeAgo(a.last_seen)}</span>
                </div>
              ))}
              {(summary.by_actor || []).length === 0 && (
                <p className="text-sm text-[#6F8FA8]">Nothing recorded yet.</p>
              )}
            </div>
          </div>
          <div className="bg-white border border-[#E4E7EA] rounded-2xl p-5">
            <p className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold mb-3">
              Top actions (30d)
            </p>
            <div className="space-y-2">
              {(summary.by_action || []).slice(0, 5).map((a) => (
                <div key={a.action} className="flex items-center justify-between text-sm">
                  <span className="text-[#0D2F4D] font-semibold">{actionMeta(a.action).label}</span>
                  <span className="text-[#6F8FA8]">{a.count}</span>
                </div>
              ))}
              {(summary.by_action || []).length === 0 && (
                <p className="text-sm text-[#6F8FA8]">Nothing recorded yet.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-[#E4E7EA] rounded-2xl p-4 mb-5 flex flex-wrap gap-3 items-center">
        <Filter size={14} className="text-[#6F8FA8]" />
        <select
          data-testid="activity-filter-action"
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="text-sm font-semibold px-3 py-1.5 rounded-full border-1.5 border-[#D6DCE3] bg-white text-[#0D2F4D]"
        >
          <option value="">All actions</option>
          {actionOptions.map((a) => <option key={a} value={a}>{actionMeta(a).label}</option>)}
        </select>
        <select
          data-testid="activity-filter-actor"
          value={actor}
          onChange={(e) => setActor(e.target.value)}
          className="text-sm font-semibold px-3 py-1.5 rounded-full border-1.5 border-[#D6DCE3] bg-white text-[#0D2F4D]"
        >
          <option value="">All admins</option>
          {actorOptions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        {(action || actor) && (
          <button
            data-testid="activity-clear-filters"
            onClick={() => { setAction(""); setActor(""); }}
            className="text-xs text-[#6F8FA8] hover:text-[#0D2F4D] underline underline-offset-2"
          >
            Clear
          </button>
        )}
      </div>

      {/* Timeline */}
      {loading ? (
        <p className="text-center text-[#6F8FA8] py-12" data-testid="activity-loading">Loading activity…</p>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-[#E4E7EA]" data-testid="activity-empty">
          <p className="text-[#6F8FA8]">No activity recorded yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E4E7EA] rounded-2xl overflow-hidden">
          <ul className="divide-y divide-[#ECF0F4]">
            {items.map((it) => {
              const meta = actionMeta(it.action);
              return (
                <li
                  key={it.id}
                  data-testid={`activity-item-${it.id}`}
                  className="p-4 md:p-5 flex flex-col md:flex-row gap-3 md:items-center hover:bg-[#F8F9FB] transition-colors"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#ECF0F4] text-[#0D2F4D] font-bold flex items-center justify-center text-xs">
                    {(it.actor_email?.[0] || "?").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#0D2F4D]">
                      <b>{it.actor_email}</b>
                      <span className={`ml-2 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${meta.tone}`}>
                        {meta.label}
                      </span>
                      {it.target_label && (
                        <span className="ml-2 text-[#6F8FA8]">
                          {it.target_type === "integration" ? "→" : "on"} <b className="text-[#0D2F4D]">{it.target_label}</b>
                        </span>
                      )}
                    </p>
                    <ChangesLine action={it.action} changes={it.changes} />
                  </div>
                  <span className="text-xs text-[#6F8FA8] flex-shrink-0" title={it.occurred_at}>
                    {timeAgo(it.occurred_at)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
